import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Slider,
  createFilterOptions,
} from '@mui/material';
import { AssetService } from '@/services/AssetService';
import { useHierarchyEntities } from '@/hooks';
import { buildLocationLabel, filterLeafLocationOptions } from '@/utils/locationHierarchy';
import { logger } from '@equip/utils/logger';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';

const log = logger.child({ component: 'MoveAssetDialog' });

interface MoveAssetDialogProps {
  open: boolean;
  onClose: () => void;
  assetIds: string[];
  catalogId?: string | null;
  onSuccess: () => void;
}

interface LocationOption {
  id: string;
  label: string;
  breadcrumb: string | null;
  identifier: string | null;
  group: string;
}

const GROUP_KNOWN = 'Known Locations';
const GROUP_ALL = 'All Locations';

export const MoveAssetDialog: React.FC<MoveAssetDialogProps> = ({
  open,
  onClose,
  assetIds,
  catalogId,
  onSuccess,
}) => {
  const [destinationLocation, setDestinationLocation] = useState<LocationOption | null>(null);
  const [preferredLocationIds, setPreferredLocationIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(0);

  const maxQuantity = assetIds.length;

  const locationQuery = useHierarchyEntities({
    dataSource: {
      endpoint: '/api/rest/data/v1/location_hierarchy',
      order: 'breadcrumb.asc',
    },
    filters: {
      select: 'id,display_name,entity_identifier,breadcrumb,node_type_name',
    },
    enabled: open,
  });

  // Fetch preferred locations when dialog opens with a catalogId
  useEffect(() => {
    if (open && catalogId) {
      AssetService.getPreferredLocations(catalogId)
        .then((ids) => {
          setPreferredLocationIds(ids);
          log.info('Loaded preferred locations', { catalogId, count: ids.length });
        })
        .catch((err) => {
          log.warn('Failed to load preferred locations', { error: String(err) });
          setPreferredLocationIds([]);
        });
    } else if (open) {
      setPreferredLocationIds([]);
    }
  }, [open, catalogId]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDestinationLocation(null);
      setError(null);
      setQuantity(maxQuantity);
    }
  }, [open, maxQuantity]);

  // Build sorted location options with group assignments (preferred first)
  const locationOptions = useMemo<LocationOption[]>(() => {
    const allOptions = (locationQuery.data ?? []).map((row) => ({
      id: row.id,
      label: buildLocationLabel(row),
      breadcrumb: row.breadcrumb ?? null,
      identifier: row.entity_identifier ?? null,
      group: preferredLocationIds.includes(row.id) ? GROUP_KNOWN : GROUP_ALL,
    }));

    const leafOptions = filterLeafLocationOptions(allOptions);

    // Sort: known locations first, then alphabetical by breadcrumb
    return leafOptions.sort((a, b) => {
      if (a.group === GROUP_KNOWN && b.group !== GROUP_KNOWN) return -1;
      if (a.group !== GROUP_KNOWN && b.group === GROUP_KNOWN) return 1;
      return (a.breadcrumb ?? a.label).localeCompare(b.breadcrumb ?? b.label);
    });
  }, [locationQuery.data, preferredLocationIds]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions<LocationOption>({
        stringify: (option) =>
          [option.label, option.breadcrumb ?? '', option.identifier ?? ''].join(' '),
      }),
    [],
  );

  const handleSubmit = async () => {
    if (!destinationLocation) return;
    if (quantity <= 0) return;

    setSubmitting(true);
    setError(null);

    try {
      // Slice the assets based on the selected quantity
      const idsToMove = assetIds.slice(0, quantity);

      await AssetService.moveAssets(idsToMove, destinationLocation.id);
      onSuccess();
      onClose();
    } catch (err) {
      log.error('Move assets failed', { error: String(err) });
      setError(`Failed to move assets: ${getErrorMessage(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const assetLabel = maxQuantity === 1 ? 'asset' : 'assets';

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move Assets</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography>
            Selected Group Total:{' '}
            <strong>
              {maxQuantity} {assetLabel}
            </strong>
          </Typography>

          <Box>
            <Typography gutterBottom>Quantity to Move</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Slider
                value={quantity}
                onChange={(_, val) => setQuantity(val as number)}
                min={1}
                max={maxQuantity}
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <TextField
                value={quantity}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (!isNaN(val) && val >= 1 && val <= maxQuantity) {
                    setQuantity(val);
                  }
                }}
                type="number"
                inputProps={{ min: 1, max: maxQuantity }}
                size="small"
                sx={{ width: 80 }}
              />
            </Stack>
          </Box>

          <Autocomplete
            options={locationOptions}
            loading={locationQuery.isLoading}
            value={destinationLocation}
            onChange={(_event, newValue) => setDestinationLocation(newValue)}
            getOptionLabel={(option) => (option.breadcrumb ? option.breadcrumb : option.label)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            groupBy={(option) => option.group}
            filterOptions={filterOptions}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.breadcrumb && (
                    <Typography variant="caption" color="text.secondary">
                      {option.breadcrumb}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Destination Location"
                placeholder="Search by name, path, or ID"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {locationQuery.isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          onClick={handleSubmit}
          variant="contained"
          disabled={!destinationLocation || submitting || quantity <= 0}
        >
          {submitting ? 'Moving...' : 'Move Assets'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
